import { format, isValid } from "date-fns";
import { enums } from "../../enums/enums";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { IGuiaTarifaConcepto } from "../../entities/guias/IGuiaTarifa";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { MercanciaDatosComplementarios } from "../../entities/solicitudes/MercanciaDatosComplementarios";

const tipoUbicacionLabel = (tipoRecoleccionId?: number): string => {
    switch (tipoRecoleccionId) {
        case enums.tipoRecoleccion.ocurre:
            return traducirLabel(288);
        case enums.tipoRecoleccion.comercial:
            return traducirLabel(44);
        case enums.tipoRecoleccion.residencial:
            return traducirLabel(103);
        default:
            return "";
    }
};

const formatFechaHora = (fecha?: Date): string => {
    if (!fecha || !isValid(fecha)) {
        return "";
    }
    try {
        return format(fecha, "dd MMM yyyy h:mm a");
    } catch {
        return "";
    }
};

export const textoRadFormaEnvio = (rec: IRecoleccion): string => {
    const ubicacion = tipoUbicacionLabel(rec.tipoRecoleccionId);
    if (!ubicacion) {
        return "";
    }
    return `${traducirLabel(255)} ${ubicacion}`;
};

export const textoEadEntrega = (rec: IRecoleccion): string => {
    const ubicacion = tipoUbicacionLabel(rec.tipoRecoleccionId);
    if (!ubicacion) {
        return "";
    }
    return `${traducirLabel(115)} ${ubicacion}`;
};

export const textoSucursalOcurre = (rec: IRecoleccion): string => {
    if (rec.tipoRecoleccionId !== enums.tipoRecoleccion.ocurre) {
        return "";
    }
    return rec.sucursal?.nombre?.trim() ?? "";
};

export const textoFechaModalidadRecoleccion = (rec: IRecoleccion, esOrigen: boolean): string => {
    if (rec.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return "";
    }

    const modalidad = esOrigen ? rec.tipoDeRecoleccionOrigen : rec.tipoDeRecoleccionDestino;

    if (modalidad === 3 && rec.EsFueraHorario && rec.fechaCita) {
        const fechaBase = format(rec.fechaCita, "dd MMM yyyy");
        const de = rec.horaFHDe?.trim() ?? "";
        const hasta = rec.horaFHHasta?.trim() ?? "";
        if (de && hasta) {
            return `${fechaBase} ${de} - ${hasta}`;
        }
        return formatFechaHora(rec.fechaCita);
    }

    if (modalidad === 2 && rec.fechaCita) {
        return formatFechaHora(rec.fechaCita);
    }

    if (esOrigen && modalidad === 1 && rec.fechaRecoleccion) {
        return formatFechaHora(rec.fechaRecoleccion);
    }

    if (rec.fechaCitaCedis && isValid(rec.fechaCitaCedis)) {
        return formatFechaHora(rec.fechaCitaCedis);
    }

    if (rec.fechaCita && isValid(rec.fechaCita)) {
        return formatFechaHora(rec.fechaCita);
    }

    return "";
};

export const descripcionTipoUbicacion = (rec: IRecoleccion, esOrigen: boolean): string => {
    switch (rec.tipoRecoleccionId) {
        case enums.tipoRecoleccion.comercial:
            return traducirLabel(188);
        case enums.tipoRecoleccion.residencial:
            return traducirLabel(326);
        case enums.tipoRecoleccion.ocurre:
            return esOrigen ? traducirLabel(77) : traducirMessage(229);
        default:
            return "";
    }
};

export const etiquetaModalidadActiva = (rec: IRecoleccion, esOrigen: boolean): string => {
    if (rec.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return traducirMessage(207);
    }
    const modalidad = esOrigen ? rec.tipoDeRecoleccionOrigen : rec.tipoDeRecoleccionDestino;
    if (modalidad === 2) {
        return traducirMessage(227);
    }
    if (modalidad === 3) {
        return traducirMessage(226);
    }
    return esOrigen ? traducirMessage(235) : traducirMessage(234);
};

export const lineasHorariosAreaServicio = (rec: IRecoleccion): string[] =>
    rec.areaServicio?.grupoHorarios ?? [];

export const esModalidadNormal = (rec: IRecoleccion, esOrigen: boolean): boolean => {
    if (rec.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return false;
    }
    const modalidad = esOrigen ? rec.tipoDeRecoleccionOrigen : rec.tipoDeRecoleccionDestino;
    return modalidad === 1 || modalidad == null;
};

export const esOcurre = (rec: IRecoleccion): boolean =>
    rec.tipoRecoleccionId === enums.tipoRecoleccion.ocurre;

export const textoFormaRecoleccionEntrega = (rec: IRecoleccion, esOrigen: boolean): string =>
    esOrigen ? textoRadFormaEnvio(rec) : textoEadEntrega(rec);

type UnidadLocal = {
    unidad_id: number;
    unidad_nombre: string;
    tipo_unidad_nombre: string;
};

const leerUnidadesLocal = (): UnidadLocal[] => {
    try {
        const raw = localStorage.getItem("unidades");
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const nombreUnidadPorId = (unidadId: number): string => {
    if (!unidadId) {
        return "";
    }
    const match = leerUnidadesLocal().find(u => u.unidad_id === unidadId);
    return match?.unidad_nombre?.trim() ?? "";
};

export const textoConUnidad = (valor: number | string | null | undefined, unidad: string): string => {
    if (valor == null || valor === "") {
        return "";
    }
    return unidad ? `${valor} ${unidad}` : String(valor);
};

const textoCatalogoClave = (obj: { descripcionVista?: string; descripcion?: string; clave?: string } | null | undefined, fallbackId?: string): string => {
    if (obj?.descripcionVista?.trim()) {
        return obj.descripcionVista.trim();
    }
    if (obj?.descripcion?.trim()) {
        return obj.descripcion.trim();
    }
    if (obj?.clave?.trim()) {
        return obj.clave.trim();
    }
    return fallbackId?.trim() ?? "";
};

export const filaComplementarioResumen = (c: MercanciaDatosComplementarios): {
    cantidad: string;
    claveUnidad: string;
    claveProducto: string;
    descripcion: string;
    fraccionArancelaria: string;
    uuidComprobante: string;
    peso: string;
} => ({
    cantidad: c.cantidad != null ? String(c.cantidad) : "",
    claveUnidad: textoCatalogoClave(c.claveUnidad, c.claveUnidadId),
    claveProducto: textoCatalogoClave(c.claveProducto, c.claveProductoId),
    descripcion: c.descripcionMercancia?.trim() ?? "",
    fraccionArancelaria: textoCatalogoClave(c.claveFraccionArancelaria, c.claveFraccionArancelariaId),
    uuidComprobante: c.uuidComprobante?.trim() ?? "",
    peso: c.peso != null ? String(c.peso) : ""
});

export const fletePresentacionCliente = (flete: number, rad: number, ead: number): number =>
    flete + rad + ead;

export const iconoTipoRecoleccion = (tipoRecoleccionId?: number): string => {
    switch (tipoRecoleccionId) {
        case enums.tipoRecoleccion.comercial:
            return "icon-negocio.png";
        case enums.tipoRecoleccion.residencial:
            return "icon-home2.png";
        case enums.tipoRecoleccion.ocurre:
            return "icon-business.png";
        default:
            return "";
    }
};

export const iconoModalidadFecha = (rec: IRecoleccion, esOrigen: boolean): string => {
    if (rec.tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
        return "";
    }
    const modalidad = esOrigen ? rec.tipoDeRecoleccionOrigen : rec.tipoDeRecoleccionDestino;
    if (modalidad === 2 || modalidad === 3) {
        return "icon-calendar.png";
    }
    return "";
};

export const nombreServicioAdicional = (servicio: ServicioAdicional): string => {
    if (localStorage.getItem("idioma") === "en-US") {
        return servicio.etiquetaServicioAdicionalDto?.nombre
            || servicio.concepto?.etiquetaConcepto?.nombreConcepto
            || servicio.nombre
            || "";
    }
    return servicio.nombre || servicio.concepto?.nombreConcepto || "";
};

export const costoConceptoTarifa = (
    conceptos: IGuiaTarifaConcepto[] | undefined,
    conceptoId: number
): number | null => {
    const match = conceptos?.find(c => c.conceptoId === conceptoId);
    return match != null ? Number(match.costo) : null;
};

export const costoServicioAdicionalVisual = (
    servicio: ServicioAdicional,
    conceptos: IGuiaTarifaConcepto[] | undefined
): number => {
    const tarifado = costoConceptoTarifa(conceptos, servicio.id);
    if (tarifado != null) {
        return tarifado;
    }
    return Number(servicio.costo) || 0;
};

export const sumaServiciosAdicionalesVisual = (
    serviciosSeleccionados: ServicioAdicional[],
    conceptos: IGuiaTarifaConcepto[] | undefined
): number => {
    const idsSeleccionados = new Set(serviciosSeleccionados.map(s => s.id));
    return (conceptos ?? [])
        .filter(c => idsSeleccionados.has(c.conceptoId))
        .reduce((acc, c) => acc + (Number(c.costo) || 0), 0);
};
